import { decryptGupRequest } from '@/api/backend/gup';
import { getUserProfile, switchUserProfile } from '@/api/backend/profile';
import { i18next } from '@/i18n';
import {
  activateGupVisit,
  captureGupLanding,
  getGupProfile,
  getGupStartBlock,
  getGupVisit,
  getGupVisitGeneration,
  getPendingGupLanding,
  isNewGupLanding,
  markGupSessionReset,
  markGupSsoFailed,
  resolveGupService,
  type GupBlock,
  type GupVisit,
} from '@/lib/gup';
import { AuthService } from '@/services/authService';
import { useAuthStore } from '@/store/auth';
import { queryClient } from '@/services/queryClient';
import { isAxiosError } from 'axios';

export type GupEntry = {
  visit?: GupVisit;
  block?: GupBlock;
  failure?: 'invalid' | 'unavailable' | 'failed';
  registration?: boolean;
  login?: boolean;
};
let lastEntry: { url: string; generation: number; promise: Promise<GupEntry> } | undefined;

export const prepareGupEntry = async (landingUrl: string, processInstanceId?: string): Promise<GupEntry> => {
  if (
    lastEntry?.url === landingUrl &&
    lastEntry.generation === getGupVisitGeneration() &&
    (getPendingGupLanding() === landingUrl || !!getGupVisit())
  )
    return lastEntry.promise;
  // Identity comes from Gov.om (BRD 2.10), not from a session already open here: a new Gov.om link signs the
  // current user out first. The login page then continues with the stored landing (consumeGupSessionReset).
  const authService = useAuthStore.getState().authService;
  if (authService && !AuthService.isTokenExpired() && isNewGupLanding(landingUrl)) {
    captureGupLanding(landingUrl);
    markGupSessionReset();
    try {
      await authService.signoutForGupEntry();
    } catch {
      AuthService.clearLocalStorage();
      return { login: true };
    }
    // The page is leaving for the Keycloak logout.
    return new Promise<GupEntry>(() => undefined);
  }
  const landing = captureGupLanding(landingUrl);
  if (!landing) return { failure: 'invalid' };
  const generation = getGupVisitGeneration();
  const promise = (async (): Promise<GupEntry> => {
    try {
      const user = await getUserProfile(true);
      if (!user.profiles?.length) return { registration: true };
      useAuthStore.getState().setUser(user);
      const params = await decryptGupRequest(landing.encRequest);
      // Ignore an old response if a second Gov.om visit has replaced it.
      if (generation !== getGupVisitGeneration() || sessionStorage.getItem('gup.landingUrl') !== landing.landingUrl)
        return { failure: 'failed' };
      // Details links come with serviceId null; only a start needs the service.
      if (!processInstanceId && !resolveGupService(params.serviceId)) return { failure: 'invalid' };
      const visit = activateGupVisit(params, processInstanceId);
      await i18next.changeLanguage(visit.lang);
      const block = getGupStartBlock(visit, user);
      const profile = getGupProfile(visit, user);
      if (!block && profile && profile.id !== user.activeProfileId) {
        await switchUserProfile(profile.id);
        user.activeProfileId = profile.id;
      }
      if (generation !== getGupVisitGeneration()) return { failure: 'failed' };
      useAuthStore.getState().setUser(user);
      queryClient.setQueryData(['userProfile'], user);
      return { visit, block };
    } catch (error) {
      if (isAxiosError(error) && error.config?.url === '/profile' && error.response?.status === 404)
        return { registration: true };
      if (isAxiosError(error) && error.response?.status === 401) {
        // The SSO session is not accepted by the API: drop it and fall back to the portal login form.
        await useAuthStore
          .getState()
          .authService?.oidcClient.removeUser()
          .catch(() => undefined);
        AuthService.clearLocalStorage();
        markGupSsoFailed();
        return { login: true };
      }
      const status = isAxiosError(error) ? error.response?.status : undefined;
      return {
        visit: getGupVisit(processInstanceId),
        failure: status === 503 ? 'unavailable' : status === 400 || status === 422 ? 'invalid' : 'failed',
      };
    }
  })();
  lastEntry = { url: landingUrl, generation, promise };
  const result = await promise;
  if (lastEntry?.promise === promise && (result.registration || result.login || result.failure)) lastEntry = undefined;
  return result;
};
